import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";

type ClientStatus = "new" | "active" | "stalled" | "complete";

interface EstateReadyItem {
  label: string;
  complete: boolean;
}

interface CategoryProgress {
  name: string;
  completion: number;
  filled: number;
  total: number;
}

interface AdvisorClientDetail {
  id: string;
  name: string;
  email: string;
  activatedAt: string;
  lastLoginAt: string | null;
  status: ClientStatus;
  plannerTier: string;
  executorNamed: boolean;
  estateReadyScore: { completed: number; total: number; items: EstateReadyItem[] };
  categoryProgress: CategoryProgress[];
}

const STATUS_BADGE: Record<ClientStatus, { label: string; className: string }> = {
  new: { label: "New", className: "bg-muted text-muted-foreground" },
  active: { label: "Active", className: "bg-green-100 text-green-800" },
  stalled: { label: "Stalled", className: "bg-amber-100 text-amber-800" },
  complete: { label: "Complete", className: "bg-primary/10 text-primary" },
};

export default function AdvisorClientView({ clientId }: { clientId: string }) {
  const [, setLocation] = useLocation();

  const { data: client, isLoading, isError } = useQuery<AdvisorClientDetail>({
    queryKey: [`/api/advisor/clients/${clientId}`],
  });

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-muted-foreground">Loading client...</div>
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
        <Button type="button" variant="ghost" size="sm" onClick={() => setLocation("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to your plan
        </Button>
        <p className="text-muted-foreground">Client not found or you do not have access.</p>
      </div>
    );
  }

  const badge = STATUS_BADGE[client.status];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Back + Header */}
      <div>
        <Button type="button" variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => setLocation("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to your plan
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">{client.name}</h1>
            <p className="text-sm text-muted-foreground">{client.email}</p>
          </div>
          <Badge className={`${badge.className} border-0`}>{badge.label}</Badge>
        </div>
        <div className="flex gap-6 mt-3 text-sm text-muted-foreground">
          <span>Registered {format(new Date(client.activatedAt), "MMM d, yyyy")}</span>
          <span>
            Last active{" "}
            {client.lastLoginAt
              ? formatDistanceToNow(new Date(client.lastLoginAt), { addSuffix: true })
              : "never"}
          </span>
        </div>
      </div>

      {/* Estate Ready Score */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">How ready is it?</CardTitle>
            <span className="text-lg font-bold text-primary">
              {client.estateReadyScore.completed}
              <span className="text-sm font-normal text-muted-foreground">
                /{client.estateReadyScore.total}
              </span>
            </span>
          </div>
          <Progress
            value={(client.estateReadyScore.completed / client.estateReadyScore.total) * 100}
            className="h-1.5"
          />
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-2">
            {client.estateReadyScore.items.map(item => (
              <li key={item.label} className="flex items-center gap-2.5 text-sm">
                {item.complete ? (
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                )}
                <span className={item.complete ? "text-foreground" : "text-muted-foreground"}>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Category Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Category Progress</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-3">
            {client.categoryProgress.map(cat => (
              <li key={cat.name} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{cat.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {cat.filled}/{cat.total} fields
                  </span>
                </div>
                <Progress value={cat.completion} className="h-1" />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Account Info</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Plan</dt>
              <dd className="font-medium mt-0.5 capitalize">{client.plannerTier}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Executor Named</dt>
              <dd className={`font-medium mt-0.5 ${client.executorNamed ? "text-primary" : "text-muted-foreground"}`}>
                {client.executorNamed ? "Yes" : "Not yet"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="mt-0.5">
                <Badge className={`${badge.className} border-0 text-xs`}>{badge.label}</Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
